import { NgClass } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { AuthorizationService } from '@app/core/auth/authorization.service';
import { Dokument } from '@app/shared/classes/dokument';
import { DokumentTyp, DokumentTypId } from '@app/shared/classes/dokument-typ';
import { downloadFile } from '@app/shared/download-utils';
import { InjectUrlPipe } from '@app/shared/pipes/inject-url.pipe';
import { MarkedPipe } from '@app/shared/pipes/marked.pipe';
import { SettingPipe } from '@app/shared/pipes/setting.pipe';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';
import { rxResource } from "@angular/core/rxjs-interop";

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'dokument',
  templateUrl: './dokument.component.html',
  imports: [
    InjectUrlPipe,
    MarkedPipe,
    MatTooltip,
    NgClass,
    SettingPipe,
    TranslatePipe,
  ]
})
export class DokumentComponent {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);

  private readonly fileInput = viewChild.required<ElementRef>('fileInput');
  readonly meldungId = input.required<number>();
  protected readonly unsavedDokumente = signal<Dokument[]>([]);

  protected readonly dokumente = rxResource({
    defaultValue: [],
    params: () => {
      const id = this.meldungId();
      return id ? { id } : undefined;
    },
    stream: ({ params }) => this.backendService.listDokumente(params.id)
  });

  private readonly dokumentTypen = rxResource({
    defaultValue: [],
    params: () => {
      const id = this.meldungId();
      return id ? { id } : undefined;
    },
    stream: ({ params }) => this.backendService.listAvailableUploads(params.id)
  });

  private readonly remainingUploads = computed(() => {
    const existingTypen: number[] = [
      ...this.dokumente.value().map( it => it.typ.id ),
      ...this.unsavedDokumente().map( it => it.typ.id )
    ];
    let remainingUploads = new Map<DokumentTyp, number>();
    this.dokumentTypen.value()?.forEach( typ => {
      let existing = existingTypen.filter( it => it === typ.id ).length;
      let remaining = typ.maxUploadCount - existing;
      if (remaining > 0) {
        remainingUploads.set(typ, remaining);
      }
    });
    return remainingUploads;
  });

  private readonly existingUploads = computed(() => {
    const existing = new Set([
      ...this.dokumente.value().map( it => it.typ.id ),
      ...this.unsavedDokumente().map( it => it.typ.id )
    ]);
    return this.dokumentTypen.value().filter( it => existing.has(it.id) );
  });

  protected readonly availableUploads = computed(() => Array.from(this.remainingUploads().keys()));

  private selectedUpload   : DokumentTyp | null = null;
  protected readonly errorMessage = signal<string[]>([]);

  readonly delete = output<void>();
  readonly preventRedirect = output<boolean>();

  constructor() {
    effect(() => this.preventRedirect.emit(this.unsavedDokumente().length > 0));
    effect(() => {
      const id = this.meldungId();
      if (id) {
        this.unsavedDokumente.set([]);
        this.errorMessage.set([]);
      }
    })
  }

  refresh(): void {
    this.errorMessage.set([]);
    this.dokumente.reload();
    this.dokumentTypen.reload();
  }

  selectDokument(typ: DokumentTyp): void {
    this.selectedUpload = typ;
    this.fileInput().nativeElement.multiple = (this.remainingUploads().get(typ) ?? 0) > 1 ? 'multiple' : null;
    this.fileInput().nativeElement.click();
  }

  uploadDokument(event: Event): void {
    if (!this.selectedUpload) {
      // Should never happen
      throw new Error("Upload type not defined yet");
    }
    const input = event.target as HTMLInputElement;
    const files: FileList = input.files ?? new FileList();
    const maxFiles = this.remainingUploads().get(this.selectedUpload) ?? 0
    if (files.length > maxFiles) {
      this.errorMessage.set([this.translateService.instant('dokument.maxCount', {
        max   : maxFiles,
        count : files.length
      })]);
    } else if (files.length) {
      this.errorMessage.set([]);
      for (let file of files) {
        this.backendService.uploadDokument(this.selectedUpload, file)
          .subscribe({
            next: dokument => this.unsavedDokumente.update( it => [...it, dokument] ),
            error: () => this.errorMessage.update( it => [
              ...it,
              this.translateService.instant('dokument.failedUpload', {
                name: file.name
              })
            ])
          });
      }
      this.fileInput().nativeElement.value = null;
    }
  }

  saveDokumente(): void {
    if (!this.meldungId()) {
      // Should never happen
      throw new Error("Meldung not selected");
    }
    if (this.unsavedDokumente().length) {
      let dokumentIds = this.unsavedDokumente().map( it => it.id );
      this.backendService.linkDokumente(this.meldungId(), dokumentIds)
        .subscribe({
          next: dokumente => {
            this.unsavedDokumente.set([]);
            this.dokumente.set(dokumente);
          },
          error: console.error
        });
    }
  }

  downloadDokument(dokument: Dokument): void {
    this.backendService.downloadDokument(dokument)
      .subscribe({
        next: response => {
          downloadFile(response);
          if (dokument.highlight) {
            this.refresh();
          }
        },
        error: console.error
      });
  }

  markUnread(dokument: Dokument): void {
    this.backendService.markUnread(dokument)
      .subscribe( () => this.refresh() );
  }

  canDelete(dokument: Dokument): boolean {
    return dokument.typ.canDelete && (dokument.temporary || this.authorizationService.isBlv())
  }

  deleteDokument(dokument: Dokument): void {
    Swal.fire({
      text: this.translateService.instant('dokument.delete.message', {name: dokument.name}),
      icon: 'warning',
      buttonsStyling: true,
      showCancelButton: true,
      confirmButtonText: this.translateService.instant('dokument.delete.yes'),
      cancelButtonText: this.translateService.instant('global.cancel')
    }).then((result) => {
      if (result.value) {
        this.backendService.deleteDokument(dokument)
          .subscribe( () => this.removeDeletedDokument(dokument));
      }
      this.delete.emit();
    });
  }

  private removeDeletedDokument(dokument: Dokument): void {
    if (this.unsavedDokumente().includes(dokument)) {
      this.unsavedDokumente.update( list => list.filter( it => it !== dokument ) );
    } else {
      this.refresh();
    }
  }

  get showAlertImmunologisch(): boolean {
    return this.availableUploads().some( it => it.id === DokumentTypId.LABORANALYSEN_AUTOVAKZINE );
  }

  get showAlertLieferEngpass(): boolean {
    return this.availableUploads().some( it => it.id === DokumentTypId.BESTAETIGUNG_LIEFERENGPASS );
  }

  get iconAlertImmunologisch(): string {
    return this.existingUploads().some( it => it.id === DokumentTypId.LABORANALYSEN_AUTOVAKZINE )
      ? ''
      : 'icon icon--before icon--exclam red';
  }

  get iconAlertLieferEngpass(): string {
    return this.existingUploads().some( it => it.id === DokumentTypId.BESTAETIGUNG_LIEFERENGPASS )
      ? ''
      : 'icon icon--before icon--exclam red';
  }
}
